package waldo.sankan.lineamunicipalkmp.web.services

import androidx.compose.foundation.text2.input.delete
import androidx.compose.runtime.NoLiveLiterals
import kotlinx.coroutines.await
import waldo.sankan.lineamunicipalkmp.model.*
import waldo.sankan.lineamunicipalkmp.web.screens.NuevaIncidenciaManual
import kotlin.js.Date
import kotlin.js.Promise
import org.w3c.files.File
import kotlin.js.then
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.attributes.ref
import org.jetbrains.compose.web.attributes.src
import org.jetbrains.compose.web.attributes.target
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

@NoLiveLiterals
class FirebaseWebService {

    val db = js("firebase.firestore()")
    private val storage = js("firebase.storage()")

    private fun normalizeComunaRef(ref: String?): String? {
        if (ref.isNullOrBlank()) return ref
        if (ref == "TODAS" || ref == "CHILE" || ref == "GLOBAL" || ref == "General") return ref
        // 🛡️ SANKAN PROTOCOL: Devolvemos siempre el ID puro para compatibilidad total App/Web
        return ref.split("/").last().trim()
    }

    private fun mapTimestampToLong(timestamp: dynamic): Long? {
        if (timestamp == null) return null
        if (js("typeof timestamp") == "number") return (timestamp as Number).toLong()
        if (timestamp.seconds != null && js("typeof timestamp.seconds") == "number") {
            return (timestamp.seconds as Number).toLong() * 1000
        }
        return null
    }

    private fun toFirebaseTimestamp(date: dynamic): dynamic {
        if (date == null) return null
        return js("firebase.firestore.Timestamp.fromDate(new Date(date))")
    }

    private fun mapGeoPoint(geoPoint: dynamic, textLocation: String?): GeoPoint? {
        // 🛡️ REGLA DE ORO DE WALDO: La verdad absoluta está en ubicacionTexto (Prioridad #1)
        if (textLocation != null && (textLocation.contains("Lat:") || textLocation.contains("-"))) {
            try {
                // Estandarizamos comas a puntos y extraemos los números decimales
                val standardized = textLocation.replace(",", ".")
                val matches = Regex("-?\\d+\\.\\d+").findAll(standardized).toList()
                
                if (matches.size >= 2) {
                    val lat = matches[0].value.toDouble()
                    val lng = matches[1].value.toDouble()
                    // Si el texto tiene coordenadas válidas, las usamos de inmediato
                    if (lat != 0.0 && lng != 0.0) return GeoPoint(lat, lng)
                }
            } catch (e: Exception) {
                // Fallback silencioso si el texto no es parseable
            }
        }

        // 2. Prioridad Secundaria: Objeto GeoPoint técnico (Solo si el texto falla)
        if (geoPoint != null) {
            val lat = (geoPoint.latitude ?: geoPoint._lat) as? Double
            val lng = (geoPoint.longitude ?: geoPoint._long) as? Double
            if (lat != null && lng != null) return GeoPoint(lat, lng)
        }

        return null
    }

    private fun mapToCambioEstadoList(history: dynamic): List<CambioEstadoIncidencia> {
        if (history == null || js("Array.isArray(history)") == false) return emptyList()
        return (history as Array<dynamic>).map { item ->
            CambioEstadoIncidencia(
                estado = item.estado as? String ?: "",
                fecha = mapTimestampToLong(item.fecha),
                usuarioRef = item.usuarioRef as? String,
                usuarioNombre = item.usuarioNombre as? String,
                rolUsuario = item.rolUsuario as? String
            )
        }.toList()
    }

    private fun mapToComentarioList(comments: dynamic): List<ComentarioIncidencia> {
        if (comments == null || js("Array.isArray(comments)") == false) return emptyList()
        return (comments as Array<dynamic>).map { item ->
            ComentarioIncidencia(
                texto = item.texto as? String ?: "",
                fecha = mapTimestampToLong(item.fecha),
                usuarioRef = item.usuarioRef as? String,
                usuarioNombre = item.usuarioNombre as? String,
                rolUsuario = item.rolUsuario as? String
            )
        }.toList()
    }

    @NoLiveLiterals
    private suspend fun encontrarSolucionadorDisponible(tipoIncidenciaRef: String, comunaRef: String): dynamic {
        return try {
            val promise = db.collection("solucionadores")
                .where("tipoIncidenciaAsignadoRef", "==", tipoIncidenciaRef)
                .where("comunaAsignadaRef", "==", comunaRef)
                .where("estaActivo", "==", true)
                .limit(1)
                .get() as Promise<dynamic>
            val snapshot = promise.await()
            if (!snapshot.empty) snapshot.docs[0] else null
        } catch (e: Exception) {
            null
        }
    }

    @NoLiveLiterals
    suspend fun crearIncidenciaManual(datos: NuevaIncidenciaManual, autor: Administrador, imagen: File?) { 
        val nuevaIncidenciaRef = db.collection("incidencias").doc()
        val incidenciaData = js("{}")
        var urlEvidencia: String? = null
        if (imagen != null) {
            try {
                val path = "incidencias_manuales/${nuevaIncidenciaRef.id}/${Date.now()}-web.jpg"
                val storageRef = storage.ref(path)
                suspendCancellableCoroutine<Unit> { continuation ->
                    storageRef.put(imagen).then({ continuation.resume(Unit) }, { error -> continuation.resumeWithException(Exception(error.toString())) })
                }
                urlEvidencia = suspendCancellableCoroutine<String> { continuation ->
                    storageRef.getDownloadURL().then({ url -> continuation.resume(url as String) }, { error -> continuation.resumeWithException(Exception(error.toString())) })
                }
            } catch (e: Exception) {}
        }

        // --- 2. PREPARACIÓN DE DATOS ---
        val solucionadorDoc = encontrarSolucionadorDisponible(datos.tipoIncidenciaRef, datos.comunaRef)
        val fechaCreacion = Date.now().toLong()
        var fechaSolucion: Long? = null
        var duracionSolucionMs: Long? = null
        var estadoFinal = "EN_PROCESO"

        if (datos.observacionSolucionador != null && datos.observacionSolucionador.isNotBlank()) {
            estadoFinal = "SOLUCIONADO"
            fechaSolucion = fechaCreacion
            duracionSolucionMs = 0L
        }

        incidenciaData.id = nuevaIncidenciaRef.id
        incidenciaData.usuarioNombre = datos.nombreCiudadano
        incidenciaData.usuarioRut = datos.rutCiudadano
        incidenciaData.usuarioRef = datos.usuarioAuthUid
        incidenciaData.direccion = datos.direccion
        incidenciaData.descripcion = datos.descripcion
        incidenciaData.estado = estadoFinal
        incidenciaData.fechaCreacion = js("firebase.firestore.FieldValue.serverTimestamp()")

        incidenciaData.urlImagen = urlEvidencia
        incidenciaData.fotoUri = urlEvidencia

        incidenciaData.tipoIncidenciaDisplayName = datos.tipoIncidenciaDisplayName
        incidenciaData.tipoIncidenciaRef = datos.tipoIncidenciaRef
        
        // 🛡️ REPARACIÓN DE ADN: Si los datos manuales no traen comuna, usamos la del Autor (Admin)
        val finalComunaDisplayName = if (datos.comunaDisplayName.isBlank()) autor.comunaAsignadaDisplayName else datos.comunaDisplayName
        val finalComunaRef = if (datos.comunaRef.isBlank()) autor.comunaAsignadaRef else datos.comunaRef

        incidenciaData.comunaDisplayName = finalComunaDisplayName
        incidenciaData.comunaRef = normalizeComunaRef(finalComunaRef)

        incidenciaData.regionDisplayName = if (datos.regionDisplayName.isNullOrBlank()) autor.comunaAsignadaRef?.let { "N/A" } else datos.regionDisplayName
        incidenciaData.regionRef = datos.regionRef ?: "N/A"
        
        // 🛡️ COMPATIBILIDAD ANCESTRAL: Grabamos también en idComuna e idTipoIncidencia para Apps viejas
        incidenciaData.idComuna = normalizeComunaRef(finalComunaRef)
        incidenciaData.idTipoIncidencia = datos.tipoIncidenciaRef

        val selloOrigen = when(autor.rol) {
            "OPERADOR_DRON" -> "DRON"
            "OPERADOR_CAMARAS" -> "OPERADOR_CAMARAS"
            "OPERADORA" -> "OPERADORA"
            else -> autor.rol ?: "ADMINISTRADOR"
        }

        incidenciaData.origenReporte = selloOrigen
        incidenciaData.creadoPorId = autor.uidAuth
        incidenciaData.creadoPorNombre = autor.nombre ?: "Administrador"

        if (solucionadorDoc != null) {
            val solucionadorData = solucionadorDoc.data()
            incidenciaData.idUsuarioSolucionadorAsignado = solucionadorData.uidAuth
            incidenciaData.nombreSolucionadorAsignado = solucionadorData.nombre
            incidenciaData.sectorIncidencia = solucionadorData.sectorAsignado
            incidenciaData.observacionSolucionador = "Asignado automáticamente a ${solucionadorData.nombre}."
        } else {
            incidenciaData.idUsuarioSolucionadorAsignado = null
            incidenciaData.observacionSolucionador = "Pendiente de asignación."
        }

        if (datos.lat != null && datos.lng != null) {
            val lat = datos.lat; val lng = datos.lng
            incidenciaData.ubicacionTexto = "Lat: $lat, Lng: $lng"
            incidenciaData.ubicacionGPS = js("new firebase.firestore.GeoPoint(lat, lng)")
        }

        incidenciaData.tipificacionesDelito = emptyArray<String>()
        incidenciaData.comentarios = emptyArray<Any>()
        incidenciaData.fechaSolucion = if (fechaSolucion != null) toFirebaseTimestamp(fechaSolucion) else null
        incidenciaData.duracionSolucionMs = duracionSolucionMs

        val savePromise = nuevaIncidenciaRef.set(incidenciaData) as Promise<Unit>
        savePromise.await()
        console.log("WebService: Incidencia creada con ID: ${nuevaIncidenciaRef.id} y origen: $selloOrigen")
    }

    @NoLiveLiterals
    suspend fun crearNuevoSector(nombre: String, tipoRef: String, tipoDisplay: String, comunaRef: String, comunaDisplay: String) {
        val nuevaRef = db.collection("sectores").doc()
        val data = js("{}")
        data.id = nuevaRef.id
        data.nombre = nombre
        data.estaActivo = true
        data.tipoIncidenciaAsignadoRef = tipoRef
        data.tipoIncidenciaAsignadoDisplayName = tipoDisplay
        data.comunaAsignadaDisplayName = comunaDisplay
        data.comunaAsignadaRef = normalizeComunaRef(comunaRef)
        (nuevaRef.set(data) as kotlin.js.Promise<Unit>).await()
        console.log("WebService: Sector '$nombre' creado en $comunaDisplay para $tipoDisplay")
    }

    @NoLiveLiterals
    suspend fun eliminarSector(id: String) {
        val docRef = db.collection("sectores").doc(id)
        (docRef.delete() as Promise<Unit>).await()
        console.log("WebService: Sector $id ELIMINADO.")
    }

    @NoLiveLiterals
    suspend fun actualizarEstadoSector(id: String, estado: Boolean) {
        val docRef = db.collection("sectores").doc(id)
        val data = js("{}")
        data.estaActivo = estado
        val options = js("{}")
        options.merge = true
        (docRef.set(data, options) as Promise<Unit>).await()
    }

    @NoLiveLiterals
    suspend fun actualizarNombreSector(id: String, nuevoNombre: String) {
        val docRef = db.collection("sectores").doc(id)
        val data = js("{}")
        data.nombre = nuevoNombre
        val options = js("{}")
        options.merge = true
        (docRef.set(data, options) as Promise<Unit>).await()
    }

    @NoLiveLiterals
    suspend fun publicarNoticiaMasiva(
        titulo: String,
        contenido: String,
        imagenUrl: String?,
        categoria: String = "GENERAL", // --- NUEVO CAMPO DE ÁNIMOS ---
        comunaRef: String,
        comunaNombre: String,
        autor: String,
        sectorRef: String? = null,
        sectorDisplayName: String? = null,
        edadMinima: Int? = null,
        edadMaxima: Int? = null
    ) {
        console.log("WebService: Iniciando publicación de noticia masiva ($categoria)...")
        val nuevaNoticiaRef = db.collection("noticias").doc()
        val data = js("{}")
        data.id = nuevaNoticiaRef.id
        data.titulo = titulo
        data.contenido = contenido
        data.imagenUrl = imagenUrl
        data.categoria = categoria

        // 🛡️ PURIFICACIÓN: Forzamos el formato estándar para que nazca limpio
        val refNormalizada = normalizeComunaRef(comunaRef)
        data.comunaRef = refNormalizada
        data.comunaNombre = comunaNombre
        data.autor = autor
        data.fechaCreacion = js("firebase.firestore.FieldValue.serverTimestamp()")

        // Segmentación avanzada
        if (sectorRef != null) {
            data.sectorRef = sectorRef
            data.sectorDisplayName = sectorDisplayName
        }
        if (edadMinima != null) data.edadMinima = edadMinima
        if (edadMaxima != null) data.edadMaxima = edadMaxima

        try {
            val promise = nuevaNoticiaRef.set(data) as Promise<Unit>
            promise.await()
            console.log("WebService: Documento de noticia creado exitosamente con ID: ${nuevaNoticiaRef.id}")
        } catch (e: Exception) {
            console.error("WebService: Error al crear documento de noticia: ${e.message}")
            throw e
        }

        // INCREMENTO ATÓMICO DE CUOTA (Excepto para envíos Globales)
        val idComunaPuro = normalizeComunaRef(comunaRef)
        if (idComunaPuro != null && idComunaPuro != "TODAS" && !idComunaPuro.contains("GENERAL", ignoreCase = true)) {
            try {
                console.log("WebService: Incrementando cuota para la comuna ID: $idComunaPuro...")
                // 🛡️ REPARACIÓN: Especificamos la colección "comunas" para que la ruta sea par (comunas/ID)
                val communeDocRef = db.collection("comunas").doc(idComunaPuro)
                val updateData = js("{}")
                updateData.noticiasMensualesEnviadas = js("firebase.firestore.FieldValue.increment(1)")
                (communeDocRef.update(updateData) as Promise<Unit>).await()
                console.log("WebService: Cuota incrementada exitosamente.")
            } catch (e: Exception) {
                console.error("WebService: Error al incrementar cuota para $idComunaPuro: ${e.message}")
                // No relanzamos aquí para no fallar toda la operación si solo falla el incremento
            }
        }

        console.log("WebService: Proceso de publicación finalizado para ID: ${nuevaNoticiaRef.id}")
    }

    @NoLiveLiterals
    suspend fun fetchNoticias(comunaRef: String? = null): List<Noticia> {
        console.log("WebService: Cargando historial de noticias para soberanía: $comunaRef")
        val collection = db.collection("noticias")

        // 🛡️ DOCTRINA DE RESILIENCIA: Evitamos el requisito de índices compuestos
        // Si pedimos una comuna específica, filtramos pero ordenamos en memoria.
        // Si es GLOBAL (null o "TODAS"), usamos el orden de Firestore directo.

        return try {
            val query = if (comunaRef != null && comunaRef != "TODAS") {
                // 🛡️ REPARACIÓN: Buscamos noticias que coincidan con la referencia del admin
                // Aseguramos que el filtro use el mismo formato que se grabó (con o sin prefijo)
                val refNormalizada = normalizeComunaRef(comunaRef)
                collection.where("comunaRef", "==", refNormalizada)
            } else {
                collection.orderBy("fechaCreacion", "desc")
            }

            val promise = (query.get() as Promise<dynamic>)
            val snapshot = promise.await()
            val list = snapshot.docs.unsafeCast<Array<dynamic>>().map { doc ->
                val data = doc.data()
                Noticia(
                    id = doc.id,
                    titulo = data.titulo as? String ?: "",
                    contenido = data.contenido as? String ?: "",
                    imagenUrl = data.imagenUrl as? String,
                    comunaRef = data.comunaRef as? String,
                    comunaNombre = data.comunaNombre as? String,
                    autor = data.autor as? String,
                    fechaCreacion = mapTimestampToLong(data.fechaCreacion),
                    sectorRef = data.sectorRef as? String,
                    sectorDisplayName = data.sectorDisplayName as? String,
                    edadMinima = (data.edadMinima as? Number)?.toInt(),
                    edadMaxima = (data.edadMaxima as? Number)?.toInt()
                )
            }.toList()

            // Ordenamos en memoria si filtramos por comuna para garantizar el orden sin depender de índices de Firebase
            if (comunaRef != null && comunaRef != "TODAS") {
                list.sortedByDescending { it.fechaCreacion }
            } else {
                list
            }
        } catch (e: Exception) {
            console.error("Error al cargar noticias: ${e.message}")
            emptyList()
        }
    }

    @NoLiveLiterals
    suspend fun eliminarNoticia(id: String) {
        val docRef = db.collection("noticias").doc(id)
        (docRef.delete() as Promise<Unit>).await()
        console.log("WebService: Noticia $id eliminada.")
    }

    @NoLiveLiterals
    suspend fun fetchSectoresByComuna(comunaRef: String): List<Sector> {
        val promise = db.collection("sectores")
            .where("comunaAsignadaRef", "==", comunaRef)
            .where("estaActivo", "==", true)
            .get() as Promise<dynamic>
        val snapshot = promise.await()
        return snapshot.docs.unsafeCast<Array<dynamic>>().map { doc ->
            val data = doc.data()
            Sector(
                id = doc.id,
                nombre = data.nombre as? String ?: "",
                estaActivo = data.estaActivo as? Boolean ?: true,
                tipoRef = data.tipoIncidenciaAsignadoRef as? String ?: "",
                tipoDisplay = data.tipoIncidenciaAsignadoDisplayName as? String ?: "",
                comunaAsignadaRef = data.comunaAsignadaRef as? String
            )
        }.toList()
    }

    @androidx.compose.runtime.NoLiveLiterals
    suspend fun fetchAllIncidencias(comunaRef: String? = null): List<Incidencia> {
        val collection = db.collection("incidencias")
        // 🛡️ REPARACIÓN SOBERANÍA: Descargamos todo para permitir que el ViewModel 
        // filtre quirúrgicamente por ID o Nombre, evitando el bloqueo por casing de Firestore.
        val promise = (collection.get() as Promise<dynamic>)
        val snapshot = promise.await()
        return snapshot.docs.unsafeCast<Array<dynamic>>().map { doc ->
            val data = doc.data()
            val finalImageUrl = (data.fotoUrl ?: data.fotoUri ?: data.urlImagen) as? String
            val afterImageUrl = (data.urlImagenSolucion ?: data.fotoUriSolucion) as? String
            val droneImageUrl = data.urlImagenVerificacionDron as? String
            Incidencia(
                id = doc.id as? String ?: "",
                usuarioRef = data.usuarioRef as? String,
                usuarioNombre = data.usuarioNombre as? String,
                usuarioRut = data.usuarioRut as? String,
                tipoIncidenciaRef = data.tipoIncidenciaRef as? String,
                tipoIncidenciaDisplayName = data.tipoIncidenciaDisplayName as? String,
                comunaRef = data.comunaRef as? String,
                comunaDisplayName = data.comunaDisplayName as? String,
                regionRef = data.regionRef as? String,
                regionDisplayName = data.regionDisplayName as? String,
                descripcion = data.descripcion as? String ?: "",
                estado = data.estado as? String ?: "EN_PROCESO",
                ubicacionTexto = data.ubicacionTexto as? String,
                ubicacionGPS = mapGeoPoint(data.ubicacionGPS, data.ubicacionTexto),
                urlImagen = finalImageUrl,
                observacionSolucionador = data.observacionSolucionador as? String,
                idUsuarioSolucionadorAsignado = data.idUsuarioSolucionadorAsignado as? String,
                nombreSolucionadorAsignado = data.nombreSolucionadorAsignado as? String,
                sectorIncidencia = data.sectorIncidencia as? String,
                urlImagenSolucion = afterImageUrl,
                urlImagenVerificacionDron = droneImageUrl,
                ubicacionGPSSolucion = mapGeoPoint(data.ubicacionGPSSolucion, null),
                fechaCreacion = mapTimestampToLong(data.fechaCreacion),
                fechaActualizacion = mapTimestampToLong(data.fechaActualizacion),
                fechaSolucion = mapTimestampToLong(data.fechaSolucion),
                huboDetenidos = data.huboDetenidos as? Boolean ?: false,
                cantidadDetenidos = (data.cantidadDetenidos as? Number)?.toInt() ?: 0,
                cantidadFiscalizaciones = (data.cantidadFiscalizaciones as? Number)?.toInt() ?: 0,
                cantidadControlesIdentidad = (data.cantidadControlesIdentidad as? Number)?.toInt() ?: 0,
                duracionSolucionMs = (data.duracionSolucionMs as? Number)?.toLong(),
                idUsuariosRefuerzo = (data.idUsuariosRefuerzo as? Array<String>)?.toList() ?: emptyList(),
                historialEstados = mapToCambioEstadoList(data.historialEstados),
                comentarios = mapToComentarioList(data.comentarios),
                tipificacionesDelito = (data.tipificacionesDelito as? Array<String>)?.toList() ?: emptyList(),
                origenReporte = data.origenReporte as? String ?: "CIUDADANO",
                creadoPorId = data.creadoPorId as? String,
                creadoPorNombre = data.creadoPorNombre as? String,
                marcadoComoDuplicado = data.marcadoComoDuplicado as? Boolean ?: false,
                padreId = data.padreId as? String,
                reportesSimilaresCount = (data.reportesSimilaresCount as? Number)?.toInt() ?: 0,
                observacionAuditoria = data.observacionAuditoria as? String
            )
        }
    }

    suspend fun updateIncidenciaStatus(incidenciaId: String, nuevoEstado: String) {
        val docRef = db.collection("incidencias").doc(incidenciaId)
        val updateData = js("{}")
        updateData.estado = nuevoEstado
        updateData.fechaActualizacion = js("firebase.firestore.FieldValue.serverTimestamp()")
        val promise = docRef.update(updateData) as Promise<Unit>
        promise.await()
    }

    suspend fun addComentarioToIncidencia(incidenciaId: String, mensaje: String, rol: String): ComentarioIncidencia {
        val docRef = db.collection("incidencias").doc(incidenciaId)
        val timestampAhora = Date().getTime().toLong()
        val newComment = ComentarioIncidencia(texto = mensaje, fecha = timestampAhora, usuarioRef = "ADMIN_USER", usuarioNombre = "Administrador", rolUsuario = rol)
        val commentData = js("{}")
        commentData.texto = newComment.texto
        commentData.fecha = js("firebase.firestore.Timestamp.now()")
        commentData.usuarioRef = newComment.usuarioRef
        commentData.usuarioNombre = newComment.usuarioNombre
        commentData.rolUsuario = newComment.rolUsuario
        val firebaseFieldValue = js("firebase.firestore.FieldValue")
        val updateData = js("{}")
        updateData.comentarios = firebaseFieldValue.arrayUnion(commentData)
        updateData.fechaActualizacion = firebaseFieldValue.serverTimestamp()
        val promise = docRef.update(updateData) as Promise<Unit>
        promise.await()
        return newComment
    }

    suspend fun fetchAllComunas(): List<Comuna> {
        val promise = db.collection("comunas").get() as Promise<dynamic>
        val snapshot = promise.await()
        return snapshot.docs.unsafeCast<Array<dynamic>>().map { doc ->
            val data = doc.data()
            Comuna(
                id = doc.id as? String ?: "",
                comunaDisplayName = data.comunaDisplayName as? String ?: "",
                estaActiva = data.estaActiva as? Boolean ?: false,
                noticiasHabilitadas = data.noticiasHabilitadas as? Boolean ?: false,
                noticiasMensualesEnviadas = (data.noticiasMensualesEnviadas as? Number)?.toInt() ?: 0,
                noticiasMensualesLimite = (data.noticiasMensualesLimite as? Number)?.toInt() ?: 4,
                fechaUltimoReinicioNoticias = mapTimestampToLong(data.fechaUltimoReinicioNoticias),
                regionRef = data.regionRef as? String,
                regionDisplayName = data.regionDisplayName as? String
            )
        }.toList()
    }

    @NoLiveLiterals
    suspend fun fetchAllRegiones(): List<Region> {
        val promise = db.collection("regiones").orderBy("orden").get() as Promise<dynamic>
        val snapshot = promise.await()
        return snapshot.docs.unsafeCast<Array<dynamic>>().map { doc ->
            val data = doc.data()
            Region(
                id = doc.id as? String ?: "",
                nombre = data.nombre as? String ?: "",
                orden = (data.orden as? Number)?.toInt() ?: 0,
                estaActiva = data.estaActiva as? Boolean ?: true
            )
        }.toList()
    }

    @NoLiveLiterals
    suspend fun reiniciarCuotaComuna(id: String) {
        val docRef = db.collection("comunas").doc(id)
        val data = js("{}")
        data.noticiasMensualesEnviadas = 0
        data.fechaUltimoReinicioNoticias = js("firebase.firestore.FieldValue.serverTimestamp()")

        (docRef.update(data) as Promise<Unit>).await()
        console.log("WebService: Cuota reiniciada para comuna $id")
    }

    @NoLiveLiterals
    suspend fun actualizarEstadoComuna(id: String, estado: Boolean) {
        val docRef = db.collection("comunas").doc(id)
        val data = js("{}")
        data.estaActiva = estado

        val options = js("{}")
        options.merge = true
        (docRef.set(data, options).unsafeCast<Promise<Unit>>()).await()
    }

    @NoLiveLiterals
    suspend fun actualizarAccesoNoticiasComuna(id: String, habilitado: Boolean) {
        val docRef = db.collection("comunas").doc(id)
        val data = js("{}")
        data.noticiasHabilitadas = habilitado

        val options = js("{}")
        options.merge = true
        (docRef.set(data, options).unsafeCast<Promise<Unit>>()).await()
    }

    @NoLiveLiterals
    suspend fun actualizarLimiteNoticiasComuna(id: String, nuevoLimite: Int) {
        val docRef = db.collection("comunas").doc(id)
        val data = js("{}")
        data.noticiasMensualesLimite = nuevoLimite

        val options = js("{}")
        options.merge = true
        (docRef.set(data, options).unsafeCast<Promise<Unit>>()).await()
    }

    @NoLiveLiterals
    suspend fun getAdminProfile(uid: String): Administrador? {
        val docRef = db.collection("administradores").doc(uid)
        val snapshot = (docRef.get() as Promise<dynamic>).await()

        var adminData: dynamic = null

        if (!snapshot.exists) {
            // --- 🛡️ PROTOCOLO DE AUTO-RESCATE GENERAL ---
            if (uid == "X8YDn9yRmHVApesYxamMhgB0bgM2") {
                console.log("Mando Central: Detectado Admin General sin perfil. Re-fabricando...")
                val rescueData = js("{}")
                rescueData.uidAuth = uid
                rescueData.nombre = "Administrador General"
                rescueData.email = "admin@general.cl"
                rescueData.rol = "GENERAL"
                rescueData.estaActivo = true
                rescueData.comunaAsignadaRef = "GLOBAL"
                rescueData.comunaAsignadaDisplayName = "CHILE"
                (docRef.set(rescueData) as Promise<Unit>).await()
                adminData = rescueData
            } else {
                // Fallback para búsqueda por query si el docId no es el UID
                val querySnapshot = (db.collection("administradores")
                    .where("uidAuth", "==", uid)
                    .limit(1)
                    .get() as Promise<dynamic>).await()
                if (querySnapshot.empty) return null
                adminData = querySnapshot.docs[0].data()
            }
        } else {
            adminData = snapshot.data()
        }

        val admin = mapDataToAdmin(adminData)

        // --- 🚫 REGLA DE IMPAGO (Soberanía Territorial) ---
        if (admin.rol != "GENERAL" && admin.comunaAsignadaRef != null) {
            val comDoc = (db.collection("comunas").doc(normalizeComunaRef(admin.comunaAsignadaRef)).get() as Promise<dynamic>).await()
            if (comDoc.exists) {
                val comData = comDoc.data()
                if (comData.estaActiva == false) {
                    console.error("Acceso denegado: Comuna desactivada por falta de pago o mantenimiento.")
                    throw Exception("SERVICIO SUSPENDIDO: La municipalidad de ${admin.comunaAsignadaDisplayName} tiene el servicio desactivado. Contacte a soporte.")
                }
            }
        }

        return admin
    }

    private fun mapDataToAdmin(data: dynamic): Administrador {
        return Administrador(
            uidAuth = data.uidAuth as? String ?: "",
            nombre = data.nombre as? String,
            email = data.email as? String,
            rol = data.rol as? String ?: "COMUNAL",
            uidRol = data.uidRol as? String,
            // 🛡️ SANKAN PROTOCOL: Normalizamos a ID PURO en memoria para que las búsquedas coincidan
            comunaAsignadaRef = normalizeComunaRef(data.comunaAsignadaRef as? String),
            comunaAsignadaDisplayName = data.comunaAsignadaDisplayName as? String,
            estaActivo = data.estaActivo as? Boolean ?: true
        )
    }

    @NoLiveLiterals
    suspend fun fetchAllAdmins(comunaRef: String?): List<Administrador> {
        val collection = db.collection("administradores")

        // 🛡️ REPARACIÓN ADN: Quitamos el prefijo "comunas/" para buscar por ID puro (ej: GENERALLAGOS)
        val refLimpia = normalizeComunaRef(comunaRef)

        val query = if (refLimpia != null && refLimpia != "TODAS") {
            collection.where("comunaAsignadaRef", "==", refLimpia)
        } else {
            collection
        }

        val promise = (query.get() as Promise<dynamic>)
        val snapshot = promise.await()
        return snapshot.docs.unsafeCast<Array<dynamic>>().map { doc ->
            val data = doc.data()
            val admin = mapDataToAdmin(data)
            // 🛡️ SINCRO DE ADN: Guardamos el ID del documento en tokenFCM para no pisar el uidAuth real
            admin.tokenFCM = doc.id
            admin
        }.toList()
    }

    suspend fun fetchAllTiposIncidencia(comunaRef: String?, includeInactive: Boolean = true): List<TipoIncidencia> {
        val promise = db.collection("tipoIncidencia").get() as Promise<dynamic>
        val snapshot = promise.await()

        val adminComunaId = (comunaRef ?: "").trim().uppercase().split("/").last()
        val esGeneral = adminComunaId.isEmpty() || adminComunaId == "NULL" || adminComunaId == "GENERAL"

        val docs = snapshot.docs.unsafeCast<Array<dynamic>>()

        return docs.mapNotNull { doc ->
            val data = doc.data()
            val id = doc.id.toString()

            val docComunaRef = (data.comunaAsignadaRef?.toString() ?: "").trim().uppercase().split("/").last()
            
            // 🛡️ DOBLE CANDADO DE LECTURA: Buscamos en ambos campos para evitar 'ceguera' de estado
            val activo = (data.estaActivo as? Boolean) ?: (data.estaActiva as? Boolean) ?: true

            if (!includeInactive && !activo) return@mapNotNull null

            // --- REGLA DE FILTRADO SANKAN (SOBERANÍA TOTAL) ---
            val mostrar = if (comunaRef == null || esGeneral) {
                true
            } else {
                // Solo mostramos si coincide exactamente con la comuna asignada
                docComunaRef == adminComunaId
            }

            if (mostrar) {
                TipoIncidencia(
                    id = id,
                    nombre = data.nombre as? String ?: "",
                    nombreDisplay = data.nombreDisplay as? String ?: data.nombre as? String ?: id,
                    estaActivo = activo,
                    iconoLocalRef = data.iconoLocalRef as? String,
                    comunaAsignadaRef = data.comunaAsignadaRef as? String,
                    comunaAsignadaDisplayName = data.comunaAsignadaDisplayName as? String,
                    orden = (data.orden as? Int) ?: 0,
                    departamento = data.departamento as? String
                )
            } else null
        }.toList()
    }

    @NoLiveLiterals
    suspend fun seedFullCommune(comunaRef: String, comunaName: String) {
        console.log("Mando Central: Iniciando siembra integral para $comunaName ($comunaRef)")

        // --- 🛡️ PROTOCOLO DE REFERENCIA DINÁMICA ---
        // Buscamos los servicios que el General marcó como GLOBAL para usarlos de plantilla
        val globalSnapshot = (db.collection("tipoIncidencia").where("comunaAsignadaRef", "in", arrayOf("GLOBAL", "CHILE", "TODAS")).get() as Promise<dynamic>).await()
        val globalTemplates = globalSnapshot.docs.unsafeCast<Array<dynamic>>()

        val defaultServices = if (!globalTemplates.isEmpty()) {
            globalTemplates.map { doc ->
                val d = doc.data()
                // 🛡️ REPARACIÓN ADN: Forzamos el baseId a minúscula para que coincida con Arica (Fuente de Verdad)
                val baseIdLimpio = (d.nombre as String).lowercase()
                Triple(baseIdLimpio, d.nombreDisplay as String, d.iconoLocalRef as String)
            }
        } else {
            // Fallback histórico si no hay globales sembrados
            listOf(
                Triple("auto_abandonado", "Auto Abandonado", "grua"),
                Triple("bache", "Bache", "bache"),
                Triple("basura", "Basura", "basura"),
                Triple("iluminacion", "Iluminación", "iluminacion"),
                Triple("salvavidas", "Salvavidas", "salvavida"),
                Triple("seguridad_ciudadana", "Seguridad Ciudadana", "seguridad"),
                Triple("semaforo", "Semáforo", "semaforo"),
                Triple("emergencia_carretera", "S.O.S. Carretera", "sos"),
                Triple("seguridadadultomayor", "Seguridad Adulto Mayor", "seguridad"),
                Triple("servicio_social", "Servicio Social", "social")
            )
        }

        val idMapping = mutableMapOf<String, String>()

        // 1. Sembrar Servicios
        for ((baseId, display, iconRef) in defaultServices) {
            // 🛡️ REPARACIÓN ADN: Forzamos el ID base a minúsculas para cumplir con el estándar de Arica
            val baseLimpia = baseId.lowercase().trim()
            val idComunaPuro = comunaRef.split("/").last()
            
            // 🛡️ PROTOCOLO DE RESCATE ARICA: Forzamos la 'i' minúscula si es Arica para match con App Android
            val finalIdComuna = if (comunaName == "Arica") "kHMVPITGigENTTpNATWm" else idComunaPuro
            val docId = "${baseLimpia}_$finalIdComuna"

            // Mapeamos el ID antiguo (que usaban los delitos) al nuevo territorial para mantener compatibilidad
            val oldId = when(baseLimpia) {
                "seguridad_ciudadana" -> "VdmMNZusszd8HanMFr4S"
                "basura" -> "Xx9dXIOJmX6NQgc2a2kj"
                "bache" -> "3eTWqMVPWYP3imsojTXp"
                "iluminacion" -> "65tR5EDkoLz8VKu062Zx"
                "semaforo" -> "QfPdJJfAMM6PjMXeivJu"
                "auto_abandonado" -> "kL2Ddyw20OJHsX3iOnNA"
                "salvavidas" -> "YXJTANA7o0GGw8OgOHsI"
                else -> baseLimpia
            }
            idMapping[oldId] = docId

            val docRef = db.collection("tipoIncidencia").doc(docId)

            // Verificamos si ya existe para no duplicar ni sobreescribir configuraciones manuales
            val existe = (docRef.get().unsafeCast<Promise<dynamic>>()).await()
            if (existe.exists == true) {
                console.log(" - El servicio $display ya existe en esta comuna. Saltando...")
                continue
            }

            val data = js("{}")
            data.id = docId
            data.nombre = baseLimpia.uppercase() // El nombre interno en mayúsculas es estándar
            data.nombreDisplay = display
            data.estaActiva = true
            data.estaActivo = true
            data.iconoLocalRef = iconRef
            // 🛡️ SANKAN PROTOCOL: Pura Sangre con ID PURO para match con Apps M1
            data.comunaAsignadaRef = normalizeComunaRef(comunaRef)
            data.comunaAsignadaDisplayName = comunaName
            data.departamento = when {
                baseLimpia.contains("seguridad") || baseLimpia.contains("salvavidas") -> "SEGURIDAD"
                baseLimpia.contains("social") -> "SOCIAL"
                baseLimpia.contains("turismo") -> "TURISMO"
                else -> "DAO"
            }
            data.orden = 0

            (docRef.set(data) as Promise<Unit>).await()
            console.log(" - Servicio desplegado: $display con ID: $docId")
        }

        // 2. Sembrar Delitos (Seguridad - cat_delitos)
        val arsenal = TipificacionesRepositoryCEAD.listaMaestraDeDelitos
        for (delito in arsenal) {
            val cleanedId = delito.nombre.lowercase().replace("/", "-").replace(" ", "_").trim()
            val docId = "${cleanedId}_${comunaRef.split("/").last()}"
            val newServiceRef = idMapping[delito.tipoIncidenciaRef] ?: delito.tipoIncidenciaRef

            val docRef = db.collection("cat_delitos").doc(docId)
            
            // 🛡️ SENSOR ANTIDUPLICIDAD: Solo creamos si no existe
            val existeDelito = (docRef.get().unsafeCast<Promise<dynamic>>()).await()
            if (existeDelito.exists == true) continue

            val data = js("{}")
            data.nombre = delito.nombre
            data.familia = delito.familia
            data.grupo = delito.grupo
            data.colorHex = delito.colorHex
            data.estaActivo = true
            data.tipoIncidenciaRef = newServiceRef
            data.comunaAsignadaRef = comunaRef
            data.comunaAsignadaDisplayName = comunaName

            (docRef.set(data).unsafeCast<Promise<Unit>>()).await()
        }

        // 3. Sembrar Hallazgos DAO (cat_dao)
        val arsenalDao = TipificacionesDao.listaMaestraDao
        console.log("📦 DAO: Sembrando ${arsenalDao.size} hallazgos en cat_dao...")
        for (delito in arsenalDao) {
            val cleanedId = delito.nombre.lowercase().replace("/", "-").replace(" ", "_").trim()
            val docId = "${cleanedId}_${comunaRef.split("/").last()}"
            val newServiceRef = idMapping[delito.tipoIncidenciaRef] ?: delito.tipoIncidenciaRef

            val docRef = db.collection("cat_dao").doc(docId)
            
            // 🛡️ SENSOR ANTIDUPLICIDAD: Solo creamos si no existe
            val existeDao = (docRef.get().unsafeCast<Promise<dynamic>>()).await()
            if (existeDao.exists == true) continue

            val data = js("{}")
            data.nombre = delito.nombre
            data.familia = delito.familia
            data.grupo = delito.grupo
            data.colorHex = delito.colorHex
            data.estaActivo = true
            data.tipoIncidenciaRef = newServiceRef
            data.comunaAsignadaRef = comunaRef
            data.comunaAsignadaDisplayName = comunaName

            (docRef.set(data).unsafeCast<Promise<Unit>>()).await()
            console.log("✅ DAO OK: $docId")
        }

        console.log("¡Misión Cumplida! $comunaName ha sido inicializada con Seguridad y DAO.")
    }

    suspend fun actualizarIncidencia(incidenciaId: String, updates: Map<String, Any?>) {
        val docRef = db.collection("incidencias").doc(incidenciaId)
        val jsUpdates = js("{}")
        updates.forEach { (key, value) ->
            if (value is Number && key.contains("fecha", ignoreCase = true)) {
                jsUpdates[key] = toFirebaseTimestamp(value)
            } else {
                jsUpdates[key] = value
            }
        }
        jsUpdates.fechaActualizacion = js("firebase.firestore.FieldValue.serverTimestamp()")
        (docRef.update(jsUpdates) as Promise<Unit>).await()
    }
    @NoLiveLiterals
    suspend fun uploadSolutionPhoto(incidenciaId: String, file: File): String {
        console.log("WebService: Subiendo evidencia de solución...")

        val compressedBlob = suspendCancellableCoroutine<dynamic> { continuation ->
            val reader = js("new FileReader()")
            js("reader").onload = { event: dynamic ->
                val img = js("new Image()")
                img.onload = {
                    val canvas = js("document.createElement('canvas')")
                    val maxSize = 1024
                    var width = img.width as Int
                    var height = img.height as Int

                    if (width > height) {
                        if (width > maxSize) {
                            height = (height * maxSize / width)
                            width = maxSize
                        }
                    } else {
                        if (height > maxSize) {
                            width = (width * maxSize / height)
                            height = maxSize
                        }
                    }

                    canvas.width = width
                    canvas.height = height
                    val ctx = canvas.getContext("2d")
                    ctx.drawImage(img, 0, 0, width, height)

                    canvas.toBlob({ blob: dynamic ->
                        continuation.resume(blob)
                    }, "image/jpeg", 0.75)
                }
                img.src = event.target.result
            }
            reader.readAsDataURL(file)
        }

        val filePath = "incidencias/$incidenciaId/solucion/solucion_${Date.now()}.jpg"
        val storageRef = storage.ref(filePath)

        suspendCancellableCoroutine<Unit> { continuation ->
            storageRef.put(compressedBlob).then(
                { continuation.resume(Unit) },
                { error -> continuation.resumeWithException(Exception(error.toString())) }
            )
        }

        return suspendCancellableCoroutine<String> { continuation ->
            storageRef.getDownloadURL().then(
                { url -> continuation.resume(url as String) },
                { error -> continuation.resumeWithException(Exception(error.toString())) }
            )
        }
    }

    @NoLiveLiterals
    suspend fun uploadNoticiaPhoto(file: File): String {
        console.log("WebService: Subiendo foto de noticia...")
        val path = "noticias_photos/${Date.now()}-${file.name}"
        val storageRef = storage.ref(path)

        suspendCancellableCoroutine<Unit> { continuation ->
            storageRef.put(file).then(
                { continuation.resume(Unit) },
                { error -> continuation.resumeWithException(Exception(error.toString())) }
            )
        }

        return suspendCancellableCoroutine<String> { continuation ->
            storageRef.getDownloadURL().then(
                { url -> continuation.resume(url as String) },
                { error -> continuation.resumeWithException(Exception(error.toString())) }
            )
        }
    }

    @NoLiveLiterals
    suspend fun uploadDroneVerificationPhoto(incidenciaId: String, file: File): String {
        console.log("WebService: Iniciando procesamiento y carga de foto de dron...")

        val compressedBlob = suspendCancellableCoroutine<dynamic> { continuation ->
            val reader = js("new FileReader()")

            // --- ¡ESTA ES LA MANIOBRA VICTORIOSA! ---
            // Asignamos el evento sin usar .asDynamic() para evitar el choque del compilador
            js("reader").onload = { event: dynamic ->
                val img = js("new Image()")
                img.onload = {
                    val canvas = js("document.createElement('canvas')")
                    val maxSize = 1024
                    var width = img.width as Int
                    var height = img.height as Int

                    if (width > height) {
                        if (width > maxSize) {
                            height = (height * maxSize / width)
                            width = maxSize
                        }
                    } else {
                        if (height > maxSize) {
                            width = (width * maxSize / height)
                            height = maxSize
                        }
                    }

                    canvas.width = width
                    canvas.height = height
                    val ctx = canvas.getContext("2d")
                    ctx.drawImage(img, 0, 0, width, height)

                    canvas.toBlob({ blob: dynamic ->
                        continuation.resume(blob)
                    }, "image/jpeg", 0.75)
                }
                img.src = event.target.result
            }
            reader.readAsDataURL(file)
        }

        // El resto de la función (subida al storage) está perfecta, no necesita cambios.
        val filePath = "drone_verifications/$incidenciaId/${Date.now()}-drone.jpg"
        val storageRef = storage.ref(filePath)

        suspendCancellableCoroutine<Unit> { continuation ->
            storageRef.put(compressedBlob).then(
                {
                    console.log("WebService: Foto de dron comprimida subida a $filePath")
                    continuation.resume(Unit)
                },
                { error -> continuation.resumeWithException(Exception(error.toString())) }
            )
        }

        return suspendCancellableCoroutine<String> { continuation ->
            storageRef.getDownloadURL().then(
                { url -> continuation.resume(url as String) },
                { error -> continuation.resumeWithException(Exception(error.toString())) }
            )
        }
    }

    @NoLiveLiterals
    suspend fun actualizarTipificaciones(incidenciaId: String, nuevasTipificaciones: List<String>) {
        try {
            val docRef = db.collection("incidencias").doc(incidenciaId)
            val updates = js("({})")
            updates["tipificacionesDelito"] = nuevasTipificaciones.toTypedArray()
            updates["fechaActualizacion"] = js("firebase.firestore.FieldValue.serverTimestamp()")
            val promise = docRef.update(updates) as Promise<Unit>
            promise.await()
        } catch (e: Exception) {
            throw e
        }
    }

    suspend fun fetchIncidenciaById(id: String): Incidencia? {
        val docRef = db.collection("incidencias").doc(id)
        val promise = docRef.get() as Promise<dynamic>
        val snapshot = promise.await()
        if (snapshot.exists == false) return null
        val data = snapshot.data()

        val finalImageUrl = (data.fotoUrl ?: data.fotoUri ?: data.urlImagen) as? String
        val afterImageUrl = (data.urlImagenSolucion ?: data.fotoUriSolucion) as? String
        val droneImageUrl = data.urlImagenVerificacionDron as? String
        return Incidencia(
            id = snapshot.id as? String ?: "",
            usuarioRef = data.usuarioRef as? String,
            usuarioNombre = data.usuarioNombre as? String,
            usuarioRut = data.usuarioRut as? String,
            tipoIncidenciaRef = data.tipoIncidenciaRef as? String,
            tipoIncidenciaDisplayName = data.tipoIncidenciaDisplayName as? String,
            comunaRef = data.comunaRef as? String,
            comunaDisplayName = data.comunaDisplayName as? String,
            regionRef = data.regionRef as? String,
            regionDisplayName = data.regionDisplayName as? String,
            descripcion = data.descripcion as? String ?: "",
            estado = data.estado as? String ?: "EN_PROCESO",
            ubicacionTexto = data.ubicacionTexto as? String,
            ubicacionGPS = mapGeoPoint(data.ubicacionGPS, data.ubicacionTexto),
            urlImagen = finalImageUrl,
            observacionSolucionador = data.observacionSolucionador as? String,
            idUsuarioSolucionadorAsignado = data.idUsuarioSolucionadorAsignado as? String,
            nombreSolucionadorAsignado = data.nombreSolucionadorAsignado as? String,
            sectorIncidencia = data.sectorIncidencia as? String,
            urlImagenSolucion = afterImageUrl,
            urlImagenVerificacionDron = droneImageUrl,
            ubicacionGPSSolucion = mapGeoPoint(data.ubicacionGPSSolucion, null),
            fechaCreacion = mapTimestampToLong(data.fechaCreacion),
            fechaActualizacion = mapTimestampToLong(data.fechaActualizacion),
            fechaSolucion = mapTimestampToLong(data.fechaSolucion),
            huboDetenidos = data.huboDetenidos as? Boolean ?: false,
            cantidadDetenidos = (data.cantidadDetenidos as? Number)?.toInt() ?: 0,
            cantidadFiscalizaciones = (data.cantidadFiscalizaciones as? Number)?.toInt() ?: 0,
            cantidadControlesIdentidad = (data.cantidadControlesIdentidad as? Number)?.toInt() ?: 0,
            duracionSolucionMs = (data.duracionSolucionMs as? Number)?.toLong(),
            idUsuariosRefuerzo = (data.idUsuariosRefuerzo as? Array<String>)?.toList() ?: emptyList(),
            historialEstados = mapToCambioEstadoList(data.historialEstados),
            comentarios = mapToComentarioList(data.comentarios),
            tipificacionesDelito = (data.tipificacionesDelito as? Array<String>)?.toList() ?: emptyList(),
            origenReporte = data.origenReporte as? String ?: "CIUDADANO",
            creadoPorId = data.creadoPorId as? String,
            creadoPorNombre = data.creadoPorNombre as? String,
            marcadoComoDuplicado = data.marcadoComoDuplicado as? Boolean ?: false,
            padreId = data.padreId as? String,
            reportesSimilaresCount = (data.reportesSimilaresCount as? Number)?.toInt() ?: 0,
            observacionAuditoria = data.observacionAuditoria as? String
        )
    }

    suspend fun fetchChildIncidents(padreId: String): List<Incidencia> {
        return try {
            val query = db.collection("incidencias").where("padreId", "==", padreId).get() as Promise<dynamic>
            val snapshot = query.await()
            val docs = snapshot.docs.unsafeCast<Array<dynamic>>()
            docs.map { doc ->
                val data = doc.data()
                val finalImageUrl = (data.fotoUrl ?: data.fotoUri ?: data.urlImagen) as? String
                val afterImageUrl = (data.urlImagenSolucion ?: data.fotoUriSolucion) as? String
                val droneImageUrl = data.urlImagenVerificacionDron as? String
                Incidencia(
                    id = doc.id as? String ?: "",
                    usuarioRef = data.usuarioRef as? String,
                    usuarioNombre = data.usuarioNombre as? String,
                    usuarioRut = data.usuarioRut as? String,
                    tipoIncidenciaRef = data.tipoIncidenciaRef as? String,
                    tipoIncidenciaDisplayName = data.tipoIncidenciaDisplayName as? String,
                    comunaRef = data.comunaRef as? String,
                    comunaDisplayName = data.comunaDisplayName as? String,
                    regionRef = data.regionRef as? String,
                    regionDisplayName = data.regionDisplayName as? String,
                    descripcion = data.descripcion as? String ?: "",
                    estado = data.estado as? String ?: "EN_PROCESO",
                    ubicacionTexto = data.ubicacionTexto as? String,
                    ubicacionGPS = mapGeoPoint(data.ubicacionGPS, data.ubicacionTexto),
                    urlImagen = finalImageUrl,
                    observacionSolucionador = data.observacionSolucionador as? String,
                    idUsuarioSolucionadorAsignado = data.idUsuarioSolucionadorAsignado as? String,
                    nombreSolucionadorAsignado = data.nombreSolucionadorAsignado as? String,
                    sectorIncidencia = data.sectorIncidencia as? String,
                    urlImagenSolucion = afterImageUrl,
                    urlImagenVerificacionDron = droneImageUrl,
                    ubicacionGPSSolucion = mapGeoPoint(data.ubicacionGPSSolucion, null),
                    fechaCreacion = mapTimestampToLong(data.fechaCreacion),
                    fechaActualizacion = mapTimestampToLong(data.fechaActualizacion),
                    fechaSolucion = mapTimestampToLong(data.fechaSolucion),
                    huboDetenidos = data.huboDetenidos as? Boolean ?: false,
                    cantidadDetenidos = (data.cantidadDetenidos as? Number)?.toInt() ?: 0,
                    cantidadFiscalizaciones = (data.cantidadFiscalizaciones as? Number)?.toInt() ?: 0,
                    cantidadControlesIdentidad = (data.cantidadControlesIdentidad as? Number)?.toInt() ?: 0,
                    duracionSolucionMs = (data.duracionSolucionMs as? Number)?.toLong(),
                    idUsuariosRefuerzo = (data.idUsuariosRefuerzo as? Array<String>)?.toList() ?: emptyList(),
                    historialEstados = mapToCambioEstadoList(data.historialEstados),
                    comentarios = mapToComentarioList(data.comentarios),
                    tipificacionesDelito = (data.tipificacionesDelito as? Array<String>)?.toList() ?: emptyList(),
                    origenReporte = data.origenReporte as? String ?: "CIUDADANO",
                    creadoPorId = data.creadoPorId as? String,
                    creadoPorNombre = data.creadoPorNombre as? String,
                    marcadoComoDuplicado = data.marcadoComoDuplicado as? Boolean ?: false,
                    padreId = data.padreId as? String,
                    reportesSimilaresCount = (data.reportesSimilaresCount as? Number)?.toInt() ?: 0,
                    observacionAuditoria = data.observacionAuditoria as? String
                )
            }
        } catch (e: Exception) {
            console.error("Error fetching child incidents: ${e.message}")
            emptyList()
        }
    }

    // --- ¡EL RADAR EN TIEMPO REAL PARA LA WEB! ---
    fun listenToIncidencias(comunaRef: String? = null, onUpdate: (List<Incidencia>) -> Unit) {
        console.log("WebService: Activando Radar de Escucha en Tiempo Real (Soberanía: ${comunaRef ?: "GLOBAL/CHILE"})...")

        // --- 🛡️ DOCTRINA DE VIGILANCIA GLOBAL ---
        // Si la referencia es nula o es el comodín "CHILE", escuchamos todo el país sin filtros.
        val query = if (comunaRef.isNullOrBlank() || comunaRef == "CHILE") {
            db.collection("incidencias")
        } else {
            // Admin Territorial: Solo su comuna
            db.collection("incidencias").where("comunaRef", "==", comunaRef)
        }

        query.onSnapshot({ snapshot ->
            val incidencias = snapshot.docs.unsafeCast<Array<dynamic>>().map { doc ->
                val data = doc.data()
                val finalImageUrl = (data.fotoUrl ?: data.fotoUri ?: data.urlImagen) as? String
                val afterImageUrl = (data.urlImagenSolucion ?: data.fotoUriSolucion) as? String
                val droneImageUrl = data.urlImagenVerificacionDron as? String

                Incidencia(
                    id = doc.id as? String ?: "",
                    usuarioRef = data.usuarioRef as? String,
                    usuarioNombre = data.usuarioNombre as? String,
                    usuarioRut = data.usuarioRut as? String,
                    tipoIncidenciaRef = data.tipoIncidenciaRef as? String,
                    tipoIncidenciaDisplayName = data.tipoIncidenciaDisplayName as? String,
                    comunaRef = data.comunaRef as? String,
                    comunaDisplayName = data.comunaDisplayName as? String,
                    regionRef = data.regionRef as? String,
                    regionDisplayName = data.regionDisplayName as? String,
                    descripcion = data.descripcion as? String ?: "",
                    estado = data.estado as? String ?: "EN_PROCESO",
                    ubicacionTexto = data.ubicacionTexto as? String,
                    ubicacionGPS = mapGeoPoint(data.ubicacionGPS, data.ubicacionTexto),
                    // 🛡️ REPARACIÓN RADAR HÍBRIDO: Unificamos urlImagen y fotoUrl
                    urlImagen = (data.urlImagen as? String) ?: (data.fotoUrl as? String) ?: (data.fotoUri as? String),
                    observacionSolucionador = data.observacionSolucionador as? String,
                    nombreSolucionadorAsignado = data.nombreSolucionadorAsignado as? String,
                    sectorIncidencia = data.sectorIncidencia as? String,
                    idUsuarioSolucionadorAsignado = data.idUsuarioSolucionadorAsignado as? String,
                    urlImagenSolucion = afterImageUrl,
                    urlImagenVerificacionDron = droneImageUrl,
                    ubicacionGPSSolucion = mapGeoPoint(data.ubicacionGPSSolucion, null),
                    fechaCreacion = mapTimestampToLong(data.fechaCreacion),
                    fechaActualizacion = mapTimestampToLong(data.fechaActualizacion),
                    fechaSolucion = mapTimestampToLong(data.fechaSolucion),
                    huboDetenidos = data.huboDetenidos as? Boolean ?: false, // <-- ¡DATO VITAL!
                    cantidadDetenidos = (data.cantidadDetenidos as? Number)?.toInt() ?: 0,
                    cantidadFiscalizaciones = (data.cantidadFiscalizaciones as? Number)?.toInt() ?: 0,
                    cantidadControlesIdentidad = (data.cantidadControlesIdentidad as? Number)?.toInt() ?: 0,
                    duracionSolucionMs = (data.duracionSolucionMs as? Number)?.toLong(),
                    idUsuariosRefuerzo = (data.idUsuariosRefuerzo as? Array<String>)?.toList() ?: emptyList(),
                    historialEstados = mapToCambioEstadoList(data.historialEstados),
                    comentarios = mapToComentarioList(data.comentarios),
                    tipificacionesDelito = (data.tipificacionesDelito as? Array<String>)?.toList() ?: emptyList(),
                    origenReporte = data.origenReporte as? String ?: "CIUDADANO",
                    creadoPorId = data.creadoPorId as? String,
                    creadoPorNombre = data.creadoPorNombre as? String
                )
            }

            onUpdate(incidencias)
        }, { error ->
            console.error("Radar Web: Error en la escucha:", error)
        })
    }

    @NoLiveLiterals
    fun listenToNoticias(comunaRef: String? = null, onUpdate: (List<Noticia>) -> Unit) {
        console.log("WebService: Activando Radar de Noticias (Soberanía: ${comunaRef ?: "GLOBAL/CHILE"})...")

        val collection = db.collection("noticias")
        val query = if (comunaRef.isNullOrBlank() || comunaRef == "CHILE" || comunaRef == "TODAS") {
            collection.orderBy("fechaCreacion", "desc")
        } else {
            collection.where("comunaRef", "==", comunaRef).orderBy("fechaCreacion", "desc")
        }

        query.onSnapshot({ snapshot ->
            val noticias = snapshot.docs.unsafeCast<Array<dynamic>>().map { doc ->
                val data = doc.data()
                Noticia(
                    id = doc.id,
                    titulo = data.titulo as? String ?: "",
                    contenido = data.contenido as? String ?: "",
                    imagenUrl = data.imagenUrl as? String,
                    comunaRef = data.comunaRef as? String,
                    comunaNombre = data.comunaNombre as? String,
                    autor = data.autor as? String,
                    fechaCreacion = mapTimestampToLong(data.fechaCreacion),
                    sectorRef = data.sectorRef as? String,
                    sectorDisplayName = data.sectorDisplayName as? String,
                    edadMinima = (data.edadMinima as? Number)?.toInt(),
                    edadMaxima = (data.edadMaxima as? Number)?.toInt()
                )
            }
            onUpdate(noticias)
        }, { error ->
            console.error("Noticias Web: Error en la escucha:", error)
        })
    }

    @NoLiveLiterals
    suspend fun fetchAllDelitosCatalog(comunaRef: String?, comunaDisplayName: String? = null): List<DelitoTipificado> {
        val collection = db.collection("cat_delitos")
        val promise = if (comunaRef != null) {
            (collection.where("comunaAsignadaRef", "==", comunaRef).get() as Promise<dynamic>)
        } else {
            (collection.get() as Promise<dynamic>)
        }
        val snapshot = promise.await()

        // 1. Cargamos lo que hay en Firebase
        val firebaseItems = snapshot.docs.unsafeCast<Array<dynamic>>().map { doc ->
            val data = doc.data()
            DelitoTipificado(
                nombre = data.nombre as? String ?: "",
                grupo = data.grupo as? String ?: "",
                familia = data.familia as? String ?: "",
                colorHex = data.colorHex as? String ?: "#000000",
                estaActivo = data.estaActivo as? Boolean ?: true,
                tipoIncidenciaRef = data.tipoIncidenciaRef as? String,
                comunaAsignadaRef = data.comunaAsignadaRef as? String,
                comunaAsignadaDisplayName = data.comunaAsignadaDisplayName as? String
            )
        }

        // 2. FUSIÓN: Usamos la Maestra como base, pero actualizamos con lo de Firebase
        val nombreComuna = comunaDisplayName ?: comunaRef?.split("/")?.last() ?: "General"

        return TipificacionesRepositoryCEAD.listaMaestraDeDelitos.map { maestro ->
            val coincidencia = firebaseItems.find { it.nombre == maestro.nombre }
            maestro.copy(
                estaActivo = coincidencia?.estaActivo ?: true,
                comunaAsignadaRef = comunaRef,
                comunaAsignadaDisplayName = nombreComuna
            )
        }
    }

    @NoLiveLiterals
    suspend fun fetchAllDaoCatalog(comunaRef: String? = null, comunaDisplayName: String? = null): List<DelitoTipificado> {
        val collection = db.collection("cat_dao")
        val promise = if (comunaRef != null) {
            (collection.where("comunaAsignadaRef", "==", comunaRef).get() as Promise<dynamic>)
        } else {
            (collection.get() as Promise<dynamic>)
        }
        val snapshot = promise.await()

        val firebaseItems = snapshot.docs.unsafeCast<Array<dynamic>>().map { doc ->
            val data = doc.data()
            DelitoTipificado(
                nombre = data.nombre as? String ?: "",
                grupo = data.grupo as? String ?: "",
                familia = data.familia as? String ?: "",
                colorHex = data.colorHex as? String ?: "#000000",
                estaActivo = data.estaActivo as? Boolean ?: true,
                tipoIncidenciaRef = data.tipoIncidenciaRef as? String,
                comunaAsignadaRef = data.comunaAsignadaRef as? String,
                comunaAsignadaDisplayName = data.comunaAsignadaDisplayName as? String
            )
        }

        val nombreComuna = comunaDisplayName ?: comunaRef?.split("/")?.last() ?: "General"

        return TipificacionesDao.listaMaestraDao.map { maestro ->
            val coincidencia = firebaseItems.find { it.nombre == maestro.nombre }
            maestro.copy(
                estaActivo = coincidencia?.estaActivo ?: true,
                comunaAsignadaRef = comunaRef,
                comunaAsignadaDisplayName = nombreComuna
            )
        }
    }

    @NoLiveLiterals
    suspend fun migrarDelitosAFirestore(comunaRef: String?, comunaName: String?) {
        console.log("🚀 INICIANDO MIGRACIÓN CRÍTICA PARA: $comunaName")

        // 🛡️ EXTRAEMOS EL ID LIMPIO (ej: "SANTIAGO") PARA EL SUFIJO DEL DOC
        val comIdSuffix = comunaRef?.split("/")?.last() ?: ""

        // --- PARTE 1: DAO ---
        val arsenalDao = TipificacionesDao.listaMaestraDao
        console.log("📦 DAO: Migrando ${arsenalDao.size} hallazgos a cat_dao...")

        for (delito in arsenalDao) {
            val cleanedId = delito.nombre.replace("/", "-")
            // 🛡️ UNIFICACIÓN: Usamos el sufijo limpio para que coincida con guardarOCrearDelito
            val docId = if (comIdSuffix.isNotBlank()) "${cleanedId}_$comIdSuffix" else cleanedId
            val docRef = db.collection("cat_dao").doc(docId)

            val data = js("{}")
            data.nombre = delito.nombre
            data.familia = delito.familia
            data.grupo = delito.grupo
            data.colorHex = delito.colorHex
            data.estaActivo = true
            data.tipoIncidenciaRef = delito.tipoIncidenciaRef
            data.comunaAsignadaRef = comunaRef // Aquí guardamos el path completo para consultas
            data.comunaAsignadaDisplayName = comunaName

            try {
                (docRef.set(data).unsafeCast<Promise<Unit>>()).await()
                console.log("✅ DAO OK: $docId")
            } catch (e: Exception) {
                console.error("❌ DAO ERROR en $docId: ${e.message}")
            }
        }

        // --- PARTE 2: SEGURIDAD ---
        val arsenalSeg = TipificacionesRepositoryCEAD.listaMaestraDeDelitos
        console.log("🛡️ SEGURIDAD: Migrando ${arsenalSeg.size} delitos a cat_delitos...")

        for (delito in arsenalSeg) {
            val cleanedId = delito.nombre.replace("/", "-")
            // 🛡️ UNIFICACIÓN: Mismo criterio de ID para Seguridad
            val docId = if (comIdSuffix.isNotBlank()) "${cleanedId}_$comIdSuffix" else cleanedId
            val docRef = db.collection("cat_delitos").doc(docId)

            val data = js("{}")
            data.nombre = delito.nombre
            data.familia = delito.familia
            data.grupo = delito.grupo
            data.colorHex = delito.colorHex
            data.estaActivo = true
            data.tipoIncidenciaRef = delito.tipoIncidenciaRef
            data.comunaAsignadaRef = comunaRef
            data.comunaAsignadaDisplayName = comunaName

            try {
                (docRef.set(data).unsafeCast<Promise<Unit>>()).await()
            } catch (e: Exception) {
                // Silencioso
            }
        }

        console.log("🏁 MIGRACIÓN FINALIZADA EXITOSAMENTE")
    }



    @NoLiveLiterals
    suspend fun guardarOCrearDelito(delito: DelitoTipificado, activo: Boolean) {
        val nombreLimpio = delito.nombre.trim().replace("/", "-").replace(" ", "_")
        // 1. ID Limpio: Extraemos solo el final de la comuna (ej: "SANTIAGO")
        val comId = delito.comunaAsignadaRef?.split("/")?.last() ?: ""
        val docId = if (comId.isNotBlank()) "${nombreLimpio}_$comId" else nombreLimpio

        // 2. Clasificación Infalible: Si está en el Arsenal de Seguridad, va a cat_delitos
        val esSeguridad = TipificacionesRepositoryCEAD.listaMaestraDeDelitos.any {
            it.nombre.trim().equals(delito.nombre.trim(), ignoreCase = true)
        } || delito.familia.uppercase().contains("SEGURIDAD")

        val collectionName = if (esSeguridad) "cat_delitos" else "cat_dao"
        val docRef = db.collection(collectionName).doc(docId)

        // 3. Definición de DATA (Esto faltaba en tu código y causaba el error)
        val data = js("{}")
        data.nombre = delito.nombre.trim()
        data.familia = delito.familia.trim()
        data.grupo = delito.grupo.trim()
        data.colorHex = delito.colorHex
        data.estaActivo = activo
        data.tipoIncidenciaRef = delito.tipoIncidenciaRef
        data.comunaAsignadaRef = delito.comunaAsignadaRef
        data.comunaAsignadaDisplayName = delito.comunaAsignadaDisplayName

        // 4. Guardado/Actualización (Sobrescribe el estado si el ID ya existe)
        val options = js("{}")
        options.merge = true
        (docRef.set(data, options).unsafeCast<Promise<Unit>>()).await()
    }
    @androidx.compose.runtime.NoLiveLiterals
    suspend fun cambiarEstadoTipoIncidencia(tipo: TipoIncidencia, nuevoEstado: Boolean) {
        val docRef = db.collection("tipoIncidencia").doc(tipo.id)
        val data = js("{}")

        // --- ACTUALIZACIÓN DUAL: Sincronizamos ambos campos para evitar desajustes ---
        data.estaActiva = nuevoEstado
        data.estaActivo = nuevoEstado

        val options = js("{}")
        options.merge = true
        (docRef.set(data, options).unsafeCast<Promise<Unit>>()).await()
    }

    @NoLiveLiterals
    suspend fun crearNuevoTipoIncidencia(
        nombre: String,
        nombreDisplay: String,
        admin: Administrador?,
        departamento: String? = null,
        comunaRef: String? = null,
        comunaNombre: String? = null
    ) {
        val baseId = nombre.lowercase().replace(" ", "_")
        val finalComunaRef = comunaRef ?: admin?.comunaAsignadaRef
        val finalComunaNombre = comunaNombre ?: admin?.comunaAsignadaDisplayName

        // 🛡️ ADN MAESTRO: Limpiamos el ID para asegurar soberanía territorial sin rutas
        val comIdSuffix = finalComunaRef?.split("/")?.last() ?: ""
        val docId = if (comIdSuffix.isNotBlank()) "${baseId}_$comIdSuffix" else baseId

        // 🎯 DETERMINACIÓN DE ICONO (Normalizada para evitar fallos por acentos)
        fun String.limpiar(): String = this.uppercase().trim()
            .replace("Á", "A").replace("É", "E").replace("Í", "I").replace("Ó", "O").replace("Ú", "U")

        val nLimpio = nombreDisplay.limpiar()
        val iconoAsignado = when {
            nLimpio.contains("VERDE") || nLimpio.contains("JARDIN") || nLimpio.contains("PLAZA") -> "areasverdes"
            nLimpio.contains("SOCIAL") -> "serviciosocial"
            nLimpio.contains("ADULTO") -> "adultomayor"
            nLimpio.contains("SOS") || nLimpio.contains("CARRETERA") || nLimpio.contains("EMERGENCIA") -> "emergencia"
            nLimpio.contains("BASURA") || nLimpio.contains("ASEO") -> "basura"
            nLimpio.contains("BACHE") -> "bache"
            nLimpio.contains("LUZ") || nLimpio.contains("ILUMINA") -> "iluminacion"
            nLimpio.contains("AUTO") -> "grua"
            nLimpio.contains("SEM") -> "semaforo"
            else -> "ic_default"
        }

        val docRef = db.collection("tipoIncidencia").doc(docId)
        
        // 🛡️ CONSTRUCCIÓN ROBUSTA: Usamos un objeto literal puro para Firestore
        val data = kotlin.js.json(
            "id" to docId,
            "nombre" to nombre.uppercase(),
            "nombreDisplay" to nombreDisplay,
            "estaActiva" to true,
            "estaActivo" to true, // 🛡️ DOBLE CANDADO SINCRONIZADO
            "iconoLocalRef" to iconoAsignado,
            "comunaAsignadaRef" to (if (finalComunaRef.isNullOrBlank()) "GLOBAL" else normalizeComunaRef(finalComunaRef)),
            "comunaAsignadaDisplayName" to (if (finalComunaNombre.isNullOrBlank()) "CHILE" else finalComunaNombre),
            "departamento" to departamento,
            "orden" to 99
        )

        (docRef.set(data).unsafeCast<Promise<Unit>>()).await()
        console.log("Mando Central: Servicio '$nombreDisplay' creado con éxito (Icono: $iconoAsignado)")
    }

    @NoLiveLiterals
    suspend fun actualizarNombreServicio(tipoId: String, nuevoNombreDisplay: String) {
        val docRef = db.collection("tipoIncidencia").doc(tipoId)
        val updates = js("{}")
        updates.nombreDisplay = nuevoNombreDisplay
        (docRef.update(updates).unsafeCast<Promise<Unit>>()).await()
    }

    @NoLiveLiterals
    suspend fun eliminarTipoIncidencia(tipoId: String) {
        val docRef = db.collection("tipoIncidencia").doc(tipoId)
        (docRef.delete().unsafeCast<Promise<Unit>>()).await()
    }


    @NoLiveLiterals
    suspend fun crearNuevoSolucionadorEspecializado(
        nombre: String, rut: String, email: String, contrasena: String,
        tipoRef: String, tipoDisplay: String,
        comunaRef: String, comunaDisplay: String
    ) {
        val appName = "Reclutamiento_" + js("Math.floor(Math.random() * 100000)")
        var secondaryApp: dynamic = null
        
        try {
            console.log("Mando Central: Iniciando tr\u00E1mite de reclutamiento para $email...")

            // 🛡️ SANKAN PROTOCOL: Clonación de instancia para evitar deslogueo del admin
            val options = js("firebase.app().options")
            secondaryApp = js("firebase.initializeApp(options, appName)")

            val secondaryAuth = secondaryApp.auth()
            
            val userCredential = (secondaryAuth.createUserWithEmailAndPassword(email, contrasena) as Promise<dynamic>).await()
            val nuevoUid = userCredential.user.uid as String

            val data = js("{}")
            data.uidAuth = nuevoUid
            data.nombre = nombre
            data.rut = rut
            data.email = email
            data.estaActivo = true
            data.sectorAsignado = "GENERAL"
            data.tipoIncidenciaAsignadoRef = tipoRef
            data.tipoIncidenciaAsignadoDisplayName = tipoDisplay
            data.comunaAsignadaDisplayName = comunaDisplay
            data.comunaAsignadaRef = normalizeComunaRef(comunaRef)

            (db.collection("solucionadores").doc(nuevoUid).set(data) as Promise<Unit>).await()
            console.log("Mando Central: Funcionario $nombre registrado con UID $nuevoUid")

        } catch (e: Exception) {
            console.error("Falla Cr\u00EDtica en Reclutamiento:", e)
            throw e
        } finally {
            if (secondaryApp != null) {
                try {
                    (secondaryApp.delete() as Promise<Unit>).await()
                    console.log("Mando Central: Sesion t\u00E9cnica de reclutamiento cerrada.")
                } catch (ex: Exception) { /* Silencioso */ }
            }
        }
    }

    @NoLiveLiterals
    suspend fun asignarSectorASolucionador(solucionadorUid: String, nuevoSectorNombre: String, nuevoSectorRef: String, nuevoSectorTipoDisplay: String) {
        val docRef = db.collection("solucionadores").doc(solucionadorUid)
        val updates = js("{}")
        updates.sectorAsignado = nuevoSectorNombre
        updates.tipoIncidenciaAsignadoRef = nuevoSectorRef
        updates.tipoIncidenciaAsignadoDisplayName = nuevoSectorTipoDisplay
        (docRef.update(updates) as Promise<Unit>).await()
        console.log("WebService: Sector '$nuevoSectorNombre' asignado al trabajador $solucionadorUid")
    }

    @NoLiveLiterals
    suspend fun actualizarNombreSolucionador(uid: String, nuevoNombre: String) {
        val docRef = db.collection("solucionadores").doc(uid)
        val updates = js("{}")
        updates.nombre = nuevoNombre
        (docRef.update(updates) as Promise<Unit>).await()
        console.log("WebService: Nombre de patrulla actualizado a '$nuevoNombre'")
    }

    @NoLiveLiterals
    suspend fun fetchAllSolucionadores(comunaRef: String?): List<UsuarioSolucionador> {
        val collection = db.collection("solucionadores")
        val promise = (collection.get() as Promise<dynamic>)
        val snapshot = promise.await()
        
        val idComunaPuro = normalizeComunaRef(comunaRef)

        return snapshot.docs.unsafeCast<Array<dynamic>>().mapNotNull { doc ->
            val data = doc.data()
            val docComunaRef = normalizeComunaRef(data.comunaAsignadaRef as? String)
            
            // 🛡️ FILTRO SOBERANO: Solo si la comuna coincide (ID Puro) o si no hay filtro
            if (idComunaPuro != null && docComunaRef != idComunaPuro) return@mapNotNull null

            UsuarioSolucionador(
                uidAuth = data.uidAuth as? String,
                nombre = data.nombre as? String,
                email = data.email as? String,
                rut = data.rut as? String,
                estaActivo = data.estaActivo as? Boolean ?: true,
                sectorAsignado = data.sectorAsignado as? String ?: "GENERAL",
                tipoIncidenciaAsignadoRef = data.tipoIncidenciaAsignadoRef as? String,
                tipoIncidenciaAsignadoDisplayName = data.tipoIncidenciaAsignadoDisplayName as? String,
                comunaAsignadaRef = data.comunaAsignadaRef as? String,
                comunaAsignadaDisplayName = data.comunaAsignadaDisplayName as? String
            )
        }.toList()
    }

    @NoLiveLiterals
    suspend fun cambiarEstadoSolucionador(solucionadorUid: String, nuevoEstado: Boolean) {
        val docRef = db.collection("solucionadores").doc(solucionadorUid)
        val data = js("{}")
        data.estaActivo = nuevoEstado
        val options = js("{}")
        options.merge = true
        (docRef.set(data, options) as Promise<Unit>).await()
    }

    @NoLiveLiterals
    suspend fun eliminarSolucionador(solucionadorUid: String) {
        val docRef = db.collection("solucionadores").doc(solucionadorUid)
        (docRef.delete() as Promise<Unit>).await()
    }

    // --- 🛡️ OPERACIÓN DE MANTENIMIENTO: NORMALIZACIÓN DE IDENTIDAD Y TERRITORIALIDAD ---
    @NoLiveLiterals
    suspend fun normalizarIdentidadesSolucionadores() {
        val snapshot = (db.collection("solucionadores").get() as Promise<dynamic>).await()
        val docs = snapshot.docs.unsafeCast<Array<dynamic>>()

        docs.forEach { doc ->
            val data = doc.data()
            val docId = doc.id as String
            val uidAuth = data.uidAuth as? String
            val comunaRef = data.comunaAsignadaRef as? String
            val oldRef = data.tipoIncidenciaAsignadoRef as? String

            if (uidAuth != null && comunaRef != null && oldRef != null) {
                // 1. Mapeo de Identidad Territorial (Modular v37)
                val baseMapping = mapOf(
                    "VdmMNZusszd8HanMFr4S" to "seguridad_ciudadana",
                    "Xx9dXIOJmX6NQgc2a2kj" to "basura",
                    "3eTWqMVPWYP3imsojTXp" to "bache",
                    "65tR5EDkoLz8VKu062Zx" to "iluminacion",
                    "QfPdJJfAMM6PjMXeivJu" to "semaforo",
                    "kL2Ddyw20OJHsX3iOnNA" to "auto_abandonado",
                    "YXJTANA7o0GGw8OgOHsI" to "salvavidas"
                )

                val basePrefix = baseMapping[oldRef]
                if (basePrefix != null) {
                    val newRef = "${basePrefix}_$comunaRef"
                    data.tipoIncidenciaAsignadoRef = newRef
                    console.log("Actualizando Referencia Modular: $oldRef -> $newRef")
                }

                // 2. Normalización de ID de Documento (Auth Sync)
                if (docId != uidAuth) {
                    console.log("Sincronizando ID Documento: $docId -> $uidAuth")
                    (db.collection("solucionadores").doc(uidAuth).set(data) as Promise<Unit>).await()
                    (db.collection("solucionadores").doc(docId).delete() as Promise<Unit>).await()
                } else {
                    // Si el ID ya era correcto, solo actualizamos los datos mapeados
                    (db.collection("solucionadores").doc(uidAuth).set(data) as Promise<Unit>).await()
                }
            }
        }
    }

    @NoLiveLiterals
    suspend fun asignarIncidenciaASolucionador(incidenciaId: String, solucionadorUid: String, nombreSolucionador: String, nombreSector: String) {
        val docRef = db.collection("incidencias").doc(incidenciaId)
        val updates = js("{}")
        updates.idUsuarioSolucionadorAsignado = solucionadorUid
        updates.nombreSolucionadorAsignado = nombreSolucionador
        updates.sectorIncidencia = nombreSector
        updates.estado = "EN_PROCESO"
        updates.fechaActualizacion = js("firebase.firestore.FieldValue.serverTimestamp()")
        (docRef.update(updates) as Promise<Unit>).await()
    }

    @NoLiveLiterals
    suspend fun fetchAllSectores(comunaRef: String?): List<Sector> {
        val collection = db.collection("sectores")
        val promise = (collection.get() as Promise<dynamic>)
        val snapshot = promise.await()
        
        val idComunaPuro = normalizeComunaRef(comunaRef)

        return snapshot.docs.unsafeCast<Array<dynamic>>().mapNotNull { doc ->
            val data = doc.data()
            val docComunaRef = normalizeComunaRef(data.comunaAsignadaRef as? String)
            
            // 🛡️ REPARACIÓN SOBERANÍA: Si filtramos por comuna, comparamos IDs PUROS
            if (idComunaPuro != null && docComunaRef != idComunaPuro) return@mapNotNull null
            
            Sector(
                id = doc.id,
                nombre = data.nombre as? String ?: "",
                estaActivo = data.estaActivo as? Boolean ?: true,
                tipoRef = data.tipoIncidenciaAsignadoRef as? String ?: "",
                tipoDisplay = data.tipoIncidenciaAsignadoDisplayName as? String ?: "",
                comunaAsignadaRef = data.comunaAsignadaRef as? String
            )
        }.toList()
    }

    @NoLiveLiterals
    suspend fun ejecutarMigracionTimestamps() {
        console.log("MIGRACIÓN: Iniciando limpieza atómica de fechas...")
        try {
            val colecciones = listOf("incidencias", "usuarios", "solucionadores", "registrosCiudadanos")

            for (nombreColeccion in colecciones) {
                console.log("MIGRACIÓN: Procesando colección: $nombreColeccion")
                val promise = db.collection(nombreColeccion).get() as Promise<dynamic>
                val snapshot = promise.await()

                snapshot.docs.unsafeCast<Array<dynamic>>().forEach { doc ->
                    val data = doc.data()
                    val updates = js("{}")
                    var huboCambios = false

                    // 🛡️ REPARACIÓN JS: Lambda para evitar error de funciones con nombre
                    val procesarFecha = { valor: dynamic, llave: String ->
                        if (valor != null && js("typeof valor === 'number'")) {
                            updates[llave] = toFirebaseTimestamp(valor)
                            huboCambios = true
                        }
                    }

                    // 1. Procesar campos raíz
                    js("Object.keys(data)").forEach { key ->
                        val value = data[key]
                        if (key.contains("fecha", ignoreCase = true) || key == "timestamp" || key == "ultimoAcceso") {
                            procesarFecha(value, key as String)
                        }
                    }

                    // 2. Procesar arrays especiales (Comentarios y Historial) en Incidencias
                    if (nombreColeccion == "incidencias") {
                        val arraysToFix = listOf("comentarios", "historialEstados")
                        arraysToFix.forEach { arrayKey ->
                            val array = data[arrayKey]
                            if (array != null && js("Array.isArray(array)")) {
                                val newArray = (array as Array<dynamic>).map { item ->
                                    val newItem = item // Clonamos referencia
                                    if (item.fecha != null && js("typeof item.fecha === 'number'")) {
                                        newItem.fecha = toFirebaseTimestamp(item.fecha)
                                        huboCambios = true
                                    }
                                    newItem
                                }.toTypedArray()
                                if (huboCambios) updates[arrayKey] = newArray
                            }
                        }
                    }

                    if (huboCambios) {
                        val docRef = db.collection(nombreColeccion).doc(doc.id)
                        (docRef.update(updates) as Promise<Unit>).then {
                            console.log("MIGRACIÓN: Documento ${doc.id} en $nombreColeccion NORMALIZADO ✅")
                        }
                    }
                }
            }
            console.log("MIGRACIÓN: ¡Misión cumplida! Base de datos sincronizada para iOS.")
        } catch (e: Exception) {
            console.error("MIGRACIÓN: Error crítico durante la limpieza: ${e.message}")
        }
    }

    @NoLiveLiterals
    suspend fun seedGabineteIntegral(comunaRef: String, comunaName: String) {
        console.log("Mando Central: Sembrando Gabinete ALEATORIO para $comunaName")

        val rolesGabinete = listOf(
            Triple("alcalde", "Alcalde / Autoridad Ejecutiva", "ALCALDE"),
            Triple("admin", "Administrador Comunal", "COMUNAL"),
            Triple("dao", "Director de Servicios Urbanos (DAO)", "DIRECCION_DAO"),
            Triple("seguridad", "Director de Seguridad Ciudadana", "SEGURIDAD_ADMIN"),
            Triple("social", "Encargado de Servicio Social", "SOCIAL_ADMIN"),
            Triple("operadora", "Operadora Telefónica Central", "OPERADORA"),
            Triple("dron", "Operador de Fiscalización Aérea (Dron)", "OPERADOR_DRON"),
            Triple("camaras", "Operador de Central de Cámaras", "OPERADOR_CAMARAS"),
            Triple("basura", "Encargado de Aseo y Basura", "BASURA"),
            Triple("bache", "Encargado de Reparación de Baches", "BACHE"),
            Triple("iluminacion", "Encargado de Alumbrado Público", "ILUMINACION"),
            Triple("semaforo", "Encargado de Red Semafórica", "SEMAFORO"),
            Triple("autoabandonado", "Encargado de Autos Abandonados", "AUTO_ABANDONADO"),
            Triple("carretera", "Encargado de Emergencias Carreteras", "EMERGENCIA_CARRETERA"),
            Triple("adultomayor", "Encargado de Seguridad Adulto Mayor", "SEGURIDAD_ADULTO_MAYOR"),
            Triple("salvavidas", "Cuerpo de Salvavidas", "SALVAVIDAS")
        )

        val domain = comunaName.lowercase().replace(" ", "")

        for ((prefix, display, rolTecnico) in rolesGabinete) {
            val emailBuscado = "$prefix@$domain.cl"

            // 🛡️ BUSCADOR DE UNICIDAD: Si el email ya existe en cualquier ID, no lo duplicamos
            val query = db.collection("administradores").where("email", "==", emailBuscado).get() as Promise<dynamic>
            val snapshot = query.await()

            if (!snapshot.empty) {
                console.log(" - El email $emailBuscado ya tiene un perfil (ID: ${snapshot.docs[0].id}). Saltando...")
                continue
            }

            // Si no existe, creamos uno nuevo con ID ALEATORIO de Firebase
            val newDocRef = db.collection("administradores").doc()
            val idComunaPuro = normalizeComunaRef(comunaRef)
            
            val data = js("{}")
            data.uidAuth = newDocRef.id 
            data.nombre = display
            data.email = emailBuscado
            data.rol = rolTecnico
            
            // 🛡️ SANKAN PROTOCOL: Vínculo "Pura Sangre" con la colección tipoIncidencia
            data.uidRol = when(rolTecnico) {
                "ALCALDE" -> "alcalde_$idComunaPuro"
                "BASURA" -> "basura_$idComunaPuro"
                "BACHE" -> "bache_$idComunaPuro"
                "ILUMINACION" -> "iluminacion_$idComunaPuro"
                "SEMAFORO" -> "semaforo_$idComunaPuro"
                "AUTO_ABANDONADO" -> "auto_abandonado_$idComunaPuro"
                "SALVAVIDAS" -> "salvavidas_$idComunaPuro"
                "SOCIAL_ADMIN" -> "servicio_social_$idComunaPuro"
                "SEGURIDAD_ADMIN" -> "seguridad_ciudadana_$idComunaPuro"
                "EMERGENCIA_CARRETERA" -> "emergencia_carretera_$idComunaPuro"
                "SEGURIDAD_ADULTO_MAYOR" -> "seguridadadultomayor_$idComunaPuro"
                else -> null
            }

            data.estaActivo = true
            data.comunaAsignadaRef = normalizeComunaRef(comunaRef)
            data.comunaAsignadaDisplayName = comunaName

            (newDocRef.set(data) as Promise<Unit>).await()
            console.log(" - Credencial sembrada (ID Aleatorio): $emailBuscado")
        }
        console.log("¡Gabinete sembrado exitosamente con IDs aleatorios!")
    }

    @NoLiveLiterals
    suspend fun activarAccesoFuncionario(email: String, adminDocId: String): String {
        val defaultPass = "111111"

        return try {
            // 🛡️ CAPTURA DE ADN DE CONFIGURACIÓN (Directa y sin intermediarios)
            val config = js("firebase.app().options")
            if (config == null) throw Exception("Error Crítico: No se pudo capturar la configuración de Firebase.")

            // Creamos una instancia aislada con nombre único basado en el tiempo
            val secondaryAppName = "App_" + js("Date.now()")
            val secondaryApp = js("firebase.initializeApp(config, secondaryAppName)")
            val secondaryAuth = secondaryApp.auth()

            var uidFinal = ""

            console.log("Mando Central: Intentando crear usuario en Firebase Auth...")
            try {
                val promise = secondaryAuth.createUserWithEmailAndPassword(email, defaultPass) as Promise<dynamic>
                val userCredential = promise.await()
                uidFinal = userCredential.user.uid as String
                console.log("✅ [AUTH] Usuario CREADO exitosamente. UID: $uidFinal")
            } catch (authError: dynamic) {
                val errorMsg = authError.toString()
                if (errorMsg.contains("email-already-in-use")) {
                    console.log("ℹ️ [AUTH] El email ya existe. Rescatando UID mediante Login...")
                    val loginPromise = secondaryAuth.signInWithEmailAndPassword(email, defaultPass) as Promise<dynamic>
                    val loginCred = loginPromise.await()
                    uidFinal = loginCred.user.uid as String
                    console.log("✅ [AUTH] UID Rescatado: $uidFinal")
                } else {
                    console.error("❌ [AUTH] Error fatal al crear usuario:", errorMsg)
                    throw Exception("Falla en Firebase Auth: $errorMsg")
                }
            }

            // 🏁 SINCRONIZACIÓN DE ADN EN FIRESTORE
            if (uidFinal.isNotBlank()) {
                console.log("Mando Central: Vinculando UID $uidFinal al documento $adminDocId...")
                val docRef = db.collection("administradores").doc(adminDocId)
                val updateData = js("{}")
                updateData.uidAuth = uidFinal
                val updatePromise = docRef.update(updateData) as Promise<Unit>
                updatePromise.await()
                console.log("✅ [FIRESTORE] Documento actualizado correctamente.")
            } else {
                throw Exception("Error: El proceso de Auth no retornó un UID válido.")
            }

            // Limpieza de seguridad
            (secondaryAuth.signOut() as Promise<Unit>).await()
            (secondaryApp.delete() as Promise<Unit>).await()

            uidFinal
        } catch (e: Exception) {
            console.error("🚨 [FALLO CRÍTICO] Error en el protocolo de activación:", e.message)
            throw e
        }
    }

    @NoLiveLiterals
    suspend fun seedNationalTerritory() {
        console.log("Mando Central: Iniciando PROTOCOLO DE UNIFICACIÓN NACIONAL (ADN SANKAN)...")

        val regionesChile = ChileTerritoryData.regiones
        val comunasChile = ChileTerritoryData.comunas
        val regionMap = mutableMapOf<String, String>() 

        // 1. Borrar basura previa (reg_XX)
        for (i in 1..16) {
            val idRaro = "reg_${i.toString().padStart(2, '0')}"
            db.collection("regiones").doc(idRaro).delete()
        }

        // 2. Procesar Regiones con IDs SAGRADOS (Protocolo de Rescate)
        for ((_, nombre, orden) in regionesChile) {
            val query = (db.collection("regiones").where("nombre", "==", nombre).get() as Promise<dynamic>).await()
            val docRef: dynamic
            val finalId: String
            
            val idAncestral = if (nombre == "Arica y Parinacota") "iZ535cpQsho9dbSd97gK" else null

            if (idAncestral != null) {
                finalId = idAncestral
                docRef = db.collection("regiones").doc(finalId)
            } else if (!query.empty) {
                val doc = query.docs[0]
                docRef = doc.ref
                finalId = doc.id
            } else {
                docRef = db.collection("regiones").doc()
                finalId = docRef.id
            }

            val data = js("{}")
            data.regionRef = finalId // 🛡️ ÚNICA REFERENCIA SAGRADA
            data.nombre = nombre
            data.orden = orden
            data.estaActiva = true
            (docRef.set(data, js("{merge: true}")) as Promise<Unit>).await()
            regionMap[nombre] = finalId
        }

        // 3. Sembrar Comunas con IDs SAGRADOS (Protocolo de Rescate)
        for ((nombre, regNombreEnData) in comunasChile) {
            val regionRealObj = regionesChile.find { it.first == regNombreEnData }
            val nombreRegionBuscado = regionRealObj?.second ?: "Chile"
            val regIdReal = regionMap[nombreRegionBuscado] ?: ""
            
            val query = (db.collection("comunas").where("comunaDisplayName", "==", nombre).get() as Promise<dynamic>).await()
            val docRef: dynamic
            val finalId: String

            val idAncestralComuna = if (nombre == "Arica") "kHMVPITGigENTTpNATWm" else null

            if (idAncestralComuna != null) {
                finalId = idAncestralComuna
                docRef = db.collection("comunas").doc(finalId)
            } else if (!query.empty) {
                val doc = query.docs[0]
                docRef = doc.ref
                finalId = doc.id
            } else {
                docRef = db.collection("comunas").doc()
                finalId = docRef.id
            }

            val data = js("{}")
            data.comunaRef = finalId // 🛡️ ÚNICA REFERENCIA SAGRADA
            data.comunaDisplayName = nombre
            data.regionRef = regIdReal
            data.regionDisplayName = nombreRegionBuscado
            
            if (query.empty) {
                data.estaActiva = (nombre == "Arica")
                data.noticiasHabilitadas = false
                data.noticiasMensualesEnviadas = 0
                data.noticiasMensualesLimite = 4
            }

            (docRef.set(data, js("{merge: true}")) as Promise<Unit>).await()
        }
        console.log("🏁 UNIFICACIÓN NACIONAL: ADN Purificado (Sin campos id redundantes).")
    }

    @NoLiveLiterals
    suspend fun crearPerfilAdminshell(nombre: String, email: String, rol: String, comunaRef: String, comunaName: String) {
        val newDocRef = db.collection("administradores").doc()
        val data = js("{}")
        data.uidAuth = newDocRef.id
        data.nombre = nombre
        data.email = email
        data.rol = rol
        data.estaActivo = true
        data.comunaAsignadaRef = normalizeComunaRef(comunaRef)
        data.comunaAsignadaDisplayName = comunaName
        (newDocRef.set(data) as Promise<Unit>).await()
        console.log("WebService: Shell de perfil ALEATORIO creado para $email")
    }

    suspend fun desvincularIncidencia(incidenciaId: String, padreIdAntiguo: String?) {
        try {
            val userName = try {
                val authObj = js("firebase.auth()")
                val user = authObj.currentUser
                if (user != null) user.email?.toString() ?: "Administrador" else "Administrador"
            } catch (e: Exception) {
                "Administrador"
            }

            val docRef = db.collection("incidencias").doc(incidenciaId)
            val updates = js("{}")
            updates["marcadoComoDuplicado"] = false
            updates["padreId"] = null
            updates["observacionAuditoria"] = "🔓 [AUDITORÍA]: Desvinculado manualmente por $userName."

            val auditComment = js("{}")
            auditComment["texto"] = "🔓 [AUDITORÍA SANKAN]: Desvinculado del caso $padreIdAntiguo por $userName"
            auditComment["fecha"] = js("Date.now()")
            auditComment["usuarioNombre"] = userName
            auditComment["rolUsuario"] = "ADMINISTRADOR"

            updates["comentarios"] = js("firebase.firestore.FieldValue.arrayUnion(auditComment)")
            (docRef.update(updates) as Promise<Unit>).await()

            if (!padreIdAntiguo.isNullOrBlank()) {
                val padreRef = db.collection("incidencias").doc(padreIdAntiguo)
                val hijosQuery = db.collection("incidencias").where("padreId", "==", padreIdAntiguo).get() as Promise<dynamic>
                val hijosSnap = hijosQuery.await()
                val countRestantes = hijosSnap.size as? Int ?: 0

                val padreUpdates = js("{}")
                padreUpdates["reportesSimilaresCount"] = countRestantes
                padreUpdates["marcadoComoDuplicado"] = false
                padreUpdates["padreId"] = null
                (padreRef.update(padreUpdates) as Promise<Unit>).await()
            }
            console.log("Incidencia $incidenciaId desvinculada por $userName con éxito.")
        } catch (e: Exception) {
            console.error("Error al desvincular incidencia: ${e.message}")
            throw e
        }
    }

    suspend fun vincularIncidencia(incidenciaId: String, inputPadreId: String) {
        try {
            val userName = try {
                val authObj = js("firebase.auth()")
                val user = authObj.currentUser
                if (user != null) user.email?.toString() ?: "Administrador" else "Administrador"
            } catch (e: Exception) {
                "Administrador"
            }

            val cleanedInput = inputPadreId.trim()
            var realPadreId = cleanedInput

            // Si es un ID corto de la tabla (ej. 8 caracteres), resolvemos al UUID completo automáticamente
            if (cleanedInput.length < 20) {
                val snap = (db.collection("incidencias").get() as Promise<dynamic>).await()
                val docsArray = js("Array.from(snap.docs)") as Array<dynamic>
                var foundId: String? = null
                docsArray.forEach { doc ->
                    val docId = doc.id?.toString() ?: ""
                    if (docId.lowercase().startsWith(cleanedInput.lowercase())) {
                        foundId = docId
                    }
                }
                if (foundId != null) {
                    realPadreId = foundId
                    console.log("🛠️ UX SANKAN: ID corto '$cleanedInput' resuelto automáticamente al UUID completo '$realPadreId'")
                } else {
                    throw Exception("No se encontró ninguna incidencia que empiece con el ID '$cleanedInput'")
                }
            }

            val docRef = db.collection("incidencias").doc(incidenciaId)
            val updates = js("{}")
            updates["marcadoComoDuplicado"] = true
            updates["padreId"] = realPadreId
            updates["observacionAuditoria"] = "🔗 [AUDITORÍA]: Vinculado manualmente por $userName al caso $realPadreId."

            val auditComment = js("{}")
            auditComment["texto"] = "🔗 [AUDITORÍA SANKAN]: Vinculado al caso $realPadreId por $userName"
            auditComment["fecha"] = js("Date.now()")
            auditComment["usuarioNombre"] = userName
            auditComment["rolUsuario"] = "ADMINISTRADOR"

            updates["comentarios"] = js("firebase.firestore.FieldValue.arrayUnion(auditComment)")
            (docRef.update(updates) as Promise<Unit>).await()

            val padreRef = db.collection("incidencias").doc(realPadreId)
            val padreSnap = (padreRef.get() as Promise<dynamic>).await()
            if (padreSnap.exists == true) {
                val data = padreSnap.data()
                val currentCount = (data.reportesSimilaresCount as? Number)?.toInt() ?: 0
                val padreUpdates = js("{}")
                padreUpdates["marcadoComoDuplicado"] = false // ¡BLINDAJE: El padre nunca es duplicado!
                padreUpdates["padreId"] = null               // ¡BLINDAJE: El padre no tiene padre!
                padreUpdates["reportesSimilaresCount"] = currentCount + 1
                (padreRef.update(padreUpdates) as Promise<Unit>).await()
            }
            console.log("Incidencia $incidenciaId vinculada al padre $realPadreId por $userName con éxito.")
        } catch (e: Exception) {
            console.error("Error al vincular incidencia: ${e.message}")
            throw e
        }
    }

    @NoLiveLiterals
    suspend fun seedGlobalTemplates() {
        console.log("Mando Central: Sembrando Parrilla Nacional (GLOBAL)...")
        val globals = listOf(
            Triple("auto_abandonado", "Auto Abandonado", "grua"),
            Triple("bache", "Bache", "bache"),
            Triple("basura", "Basura", "basura"),
            Triple("iluminacion", "Iluminación", "iluminacion"),
            Triple("salvavidas", "Salvavidas", "salvavida"),
            Triple("seguridad_ciudadana", "Seguridad Ciudadana", "seguridad"),
            Triple("semaforo", "Semáforo", "semaforo"),
            Triple("emergencia_carretera", "S.O.S. Carretera", "sos"),
            Triple("seguridadadultomayor", "Seguridad Adulto Mayor", "seguridad"),
            Triple("servicio_social", "Servicio Social", "social")
        )

        for ((idBase, display, icon) in globals) {
            val docId = "${idBase}_GLOBAL"
            val docRef = db.collection("tipoIncidencia").doc(docId)
            val data = js("{}")
            data.id = docId
            data.nombre = idBase.uppercase()
            data.nombreDisplay = display
            data.estaActiva = true
            data.estaActivo = true
            data.iconoLocalRef = icon
            data.comunaAsignadaRef = "GLOBAL"
            data.comunaAsignadaDisplayName = "CHILE"
            data.departamento = when {
                idBase.contains("seguridad") || idBase.contains("salvavidas") -> "SEGURIDAD"
                idBase.contains("social") -> "SOCIAL"
                else -> "DAO"
            }
            data.orden = 0
            (docRef.set(data, js("{merge: true}")) as Promise<Unit>).await()
            console.log(" - Plantilla GLOBAL creada: $display")
        }
    }


    @NoLiveLiterals
    suspend fun actualizarEstadoAdmin(docId: String, estado: Boolean) {
        val docRef = db.collection("administradores").doc(docId)
        val data = js("{}")
        data.estaActivo = estado
        val options = js("{}")
        options.merge = true
        (docRef.set(data, options) as Promise<Unit>).await()
    }

    @NoLiveLiterals
    suspend fun actualizarNombreAdmin(docId: String, nuevoNombre: String) {
        val docRef = db.collection("administradores").doc(docId)
        val data = js("{}")
        data.nombre = nuevoNombre
        val options = js("{}")
        options.merge = true
        (docRef.set(data, options) as Promise<Unit>).await()
    }

    @NoLiveLiterals
    suspend fun eliminarAdmin(docId: String) {
        val docRef = db.collection("administradores").doc(docId)
        (docRef.delete() as Promise<Unit>).await()
        console.log("WebService: Perfil admin $docId ELIMINADO.")
    }

    @NoLiveLiterals
    suspend fun signOut() {
        val auth = js("firebase.auth()")
        (auth.signOut() as Promise<Unit>).await()
    }

    @NoLiveLiterals
    suspend fun signInAdmin(email: String, contrasena: String): String {
        val auth = js("firebase.auth()")
        val result = (auth.signInWithEmailAndPassword(email, contrasena) as Promise<dynamic>).await()
        return result.user.uid as String
    }

    // --- 🛡️ MANDO CENTRAL: COMUNICACIÓN NACIONAL ---
    @NoLiveLiterals
    suspend fun publicarAvisoNacional(titulo: String, contenido: String) {
        val docRef = db.collection("configuracion").doc("aviso_nacional")
        val data = js("{}")
        data.titulo = titulo
        data.contenido = contenido
        data.fecha = js("firebase.firestore.FieldValue.serverTimestamp()")
        data.activo = true
        (docRef.set(data) as Promise<Unit>).await()
        console.log("Mando Central: Aviso nacional publicado.")
    }

    @NoLiveLiterals
    suspend fun fetchAvisoNacional(): dynamic {
        val docRef = db.collection("configuracion").doc("aviso_nacional")
        val snapshot = (docRef.get() as Promise<dynamic>).await()
        return if (snapshot.exists) snapshot.data() else null
    }

    @NoLiveLiterals
    suspend fun desactivarAvisoNacional() {
        val docRef = db.collection("configuracion").doc("aviso_nacional")
        val data = js("{}")
        data.activo = false
        (docRef.update(data) as Promise<Unit>).await()
    }
}
